import { supabase } from './supabase';

export interface ColumnPreferences {
  inventory: {
    visibleColumns: string[];
    columnOrder: string[];
  };
  sold: {
    visibleColumns: string[];
    columnOrder: string[];
  };
}

export interface AppSettings {
  dateFormat: string;
  dateSeparator: '/' | '-';
  currencyFormat: string;
  cardImagePreviewBehavior: 'hover' | 'click';
  cardImagePreviewSize: 'small' | 'medium' | 'large';
  defaultCalculationSettings: {
    roundingDecimals: number;
    includeFeesInTotalCost: boolean;
  };
  spreadsheetFormat: 'excel' | 'csv' | 'numbers' | 'google-sheets';
  gemDefinitions: Array<{
    id: string;
    company: string;
    grade: string;
    isGem: boolean;
  }>;
}

/**
 * Service for managing user preferences stored in the database
 * Uses localStorage as a cache for better performance
 */
export class UserPreferencesService {
  private static readonly STORAGE_KEY_PREFIX = 'sports-card-dashboard-';
  private static readonly COLUMN_PREFS_KEY = 'column-preferences';
  private static readonly APP_SETTINGS_KEY = 'app-settings';
  
  /**
   * Get column preferences for a user
   * Attempts to load from localStorage cache first, then from database
   */
  static async getColumnPreferences(userEmail: string): Promise<ColumnPreferences | null> {
    try {
      // Try localStorage first (fast cache)
      const cached = this.getFromLocalStorage<ColumnPreferences>(this.COLUMN_PREFS_KEY);
      if (cached) {
        // Load from database in background to sync any changes from other devices
        this.loadFromDatabaseToCache(userEmail, this.COLUMN_PREFS_KEY);
        return cached;
      }
      
      // Load from database if not in cache
      const { data, error } = await supabase.rpc('get_user_preference', {
        user_email: userEmail,
        pref_key: this.COLUMN_PREFS_KEY
      });
      
      if (error) {
        console.warn('Error loading column preferences from database:', error);
        return null;
      }
      
      if (data) {
        // Cache in localStorage
        this.saveToLocalStorage(this.COLUMN_PREFS_KEY, data);
        return data as ColumnPreferences;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting column preferences:', error);
      return null;
    }
  }
  
  /**
   * Save column preferences for a user
   * Saves to both database and localStorage cache
   */
  static async saveColumnPreferences(userEmail: string, preferences: ColumnPreferences): Promise<boolean> {
    try {
      // Save to database
      const { error } = await supabase.rpc('set_user_preference', {
        user_email: userEmail,
        pref_key: this.COLUMN_PREFS_KEY,
        pref_value: preferences
      });
      
      if (error) {
        console.error('Error saving column preferences to database:', error);
        return false;
      }
      
      // Also save to localStorage cache for fast access
      this.saveToLocalStorage(this.COLUMN_PREFS_KEY, preferences);
      
      return true;
    } catch (error) {
      console.error('Error saving column preferences:', error);
      return false;
    }
  }
  
  /**
   * Get app settings for a user
   * Attempts to load from localStorage cache first, then from database
   */
  static async getAppSettings(userEmail: string): Promise<AppSettings | null> {
    try {
      console.log('[UserPreferencesService] getAppSettings called for:', userEmail);
      
      // Try localStorage first (fast cache)
      const cached = this.getFromLocalStorage<AppSettings>(this.APP_SETTINGS_KEY);
      console.log('[UserPreferencesService] Cached app-settings:', cached);
      
      if (cached) {
        // Load from database in background to sync any changes from other devices
        this.loadFromDatabaseToCache(userEmail, this.APP_SETTINGS_KEY);
        return cached;
      }
      
      // Load from database if not in cache
      console.log('[UserPreferencesService] No cache, loading from database...');
      const { data, error } = await supabase.rpc('get_user_preference', {
        user_email: userEmail,
        pref_key: this.APP_SETTINGS_KEY
      });
      
      console.log('[UserPreferencesService] Database response:', { data, error });
      
      if (error) {
        console.warn('Error loading app settings from database:', error);
        return null;
      }
      
      if (data) {
        // Cache in localStorage
        this.saveToLocalStorage(this.APP_SETTINGS_KEY, data);
        return data as AppSettings;
      }
      
      console.log('[UserPreferencesService] No app-settings found in database');
      return null;
    } catch (error) {
      console.error('Error getting app settings:', error);
      return null;
    }
  }
  
  /**
   * Save app settings for a user
   * Saves to both database and localStorage cache
   */
  static async saveAppSettings(userEmail: string, settings: AppSettings): Promise<boolean> {
    try {
      // Save to database
      const { error } = await supabase.rpc('set_user_preference', {
        user_email: userEmail,
        pref_key: this.APP_SETTINGS_KEY,
        pref_value: settings
      });
      
      if (error) {
        console.error('Error saving app settings to database:', error);
        return false;
      }
      
      // Also save to localStorage cache for fast access
      this.saveToLocalStorage(this.APP_SETTINGS_KEY, settings);
      
      return true;
    } catch (error) {
      console.error('Error saving app settings:', error);
      return false;
    }
  }
  
  /**
   * Force sync from database (useful after login or when switching devices)
   */
  static async syncFromDatabase(userEmail: string): Promise<void> {
    try {
      console.log('[UserPreferencesService] syncFromDatabase called for:', userEmail);
      
      // Clear local cache first
      this.clearCache();
      console.log('[UserPreferencesService] Cache cleared');
      
      // Load all preferences from database
      const { data, error } = await supabase.rpc('get_all_user_preferences', {
        user_email: userEmail
      });
      
      console.log('[UserPreferencesService] get_all_user_preferences response:', { data, error });
      
      if (error) {
        console.error('Error syncing from database:', error);
        return;
      }
      
      if (data && Array.isArray(data)) {
        console.log('[UserPreferencesService] Found', data.length, 'preferences in database');
        // Cache each preference
        for (const pref of data) {
          console.log('[UserPreferencesService] Saving pref:', pref.preference_key, pref.preference_value);
          if (pref.preference_key && pref.preference_value) {
            this.saveToLocalStorage(pref.preference_key, pref.preference_value);
          }
        }
        console.log('Preferences synced from database');
      } else {
        console.log('[UserPreferencesService] No preferences found in database or invalid format');
      }
    } catch (error) {
      console.error('Error syncing preferences:', error);
    }
  }
  
  /**
   * Load a preference from database and update localStorage cache
   * Used for background syncing
   */
  private static async loadFromDatabaseToCache(userEmail: string, prefKey: string): Promise<void> {
    try {
      const { data, error } = await supabase.rpc('get_user_preference', {
        user_email: userEmail,
        pref_key: prefKey
      });
      
      if (!error && data) {
        this.saveToLocalStorage(prefKey, data);
      }
    } catch (error) {
      // Silent fail for background sync
      console.debug('Background sync failed:', error);
    }
  }
  
  /**
   * Get from localStorage with proper key prefix
   */
  private static getFromLocalStorage<T>(key: string): T | null {
    try {
      const fullKey = `${this.STORAGE_KEY_PREFIX}${key}`;
      const stored = localStorage.getItem(fullKey);
      if (stored) {
        return JSON.parse(stored) as T;
      }
      return null;
    } catch (error) {
      console.warn('Error reading from localStorage:', error);
      return null;
    }
  }
  
  /**
   * Save to localStorage with proper key prefix
   */
  private static saveToLocalStorage<T>(key: string, value: T): void {
    try {
      const fullKey = `${this.STORAGE_KEY_PREFIX}${key}`;
      localStorage.setItem(fullKey, JSON.stringify(value));
    } catch (error) {
      console.warn('Error writing to localStorage:', error);
    }
  }
  
  /**
   * Clear all preferences from localStorage cache
   */
  static clearCache(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.STORAGE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Error clearing cache:', error);
    }
  }
  
  /**
   * Get all user preferences from database
   * Useful for debugging or exporting settings
   */
  static async getAllPreferences(userEmail: string): Promise<Record<string, any> | null> {
    try {
      const { data, error } = await supabase.rpc('get_all_user_preferences', {
        user_email: userEmail
      });
      
      if (error) {
        console.error('Error loading all preferences:', error);
        return null;
      }
      
      if (!data || !Array.isArray(data)) {
        return null;
      }
      
      // Convert array of {preference_key, preference_value} to object
      const preferences: Record<string, any> = {};
      data.forEach((pref: any) => {
        preferences[pref.preference_key] = pref.preference_value;
      });
      
      return preferences;
    } catch (error) {
      console.error('Error getting all preferences:', error);
      return null;
    }
  }
}

